/**
 * AdminAuthProvider - Admin Authentication Context
 *
 * Dedicated authentication provider for admin portal with complete session management.
 * Mirrors the battle-tested client AuthProvider pattern but isolated for admin operations.
 *
 * Features:
 * - Admin login/logout with MFA support
 * - Enhanced JWT token management for admins
 * - Session warnings and expiry handling
 * - Cross-tab synchronization for admin sessions
 * - Security event logging for admin actions
 * - Complete isolation from user authentication
 *
 * Token Scope: admin_token (completely separate from auth_token)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import enhancedTokenManager from '../utils/enhancedTokenManager';
import apiClient from '../api/client';
import productionLogger from '../utils/monitoringFix';
import securityMonitor from '../utils/securityMonitor';
import authPerformanceMonitor from '../utils/authPerformance';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  // Core admin authentication state
  const [adminUser, setAdminUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Session management state
  const [sessionWarning, setSessionWarning] = useState(null);
  const [sessionStatus, setSessionStatus] = useState('active'); // active, warning, expiring, expired

  // Stytch session tracking
  const [stytchSessionToken, setStytchSessionToken] = useState(null);
  const [stytchSessionJwt, setStytchSessionJwt] = useState(null);

  // Refs for cleanup and preventing race conditions
  const tokenListenerRef = useRef(null);
  const mountedRef = useRef(true);
  const initializationRef = useRef(false);

  /**
   * Safe state setter that checks if component is still mounted
   */
  const safeSetState = useCallback((setter, value) => {
    if (mountedRef.current) {
      setter(value);
    }
  }, []);

  /**
   * Initialize admin authentication state and listeners
   */
  useEffect(() => {
    // Prevent multiple initializations
    if (initializationRef.current) {
      return;
    }
    initializationRef.current = true;

    // Add safety timeout to prevent hanging loading state
    const initTimeout = setTimeout(() => {
      console.warn('⚠️ AdminAuthProvider initialization timeout - forcing completion');
      if (mountedRef.current) {
        safeSetState(setIsLoading, false);
      }
    }, 10000); // 10 second timeout

    initializeAdminAuth().finally(() => {
      clearTimeout(initTimeout);
    });

    // Set up cleanup
    return () => {
      mountedRef.current = false;
      clearTimeout(initTimeout);
      if (tokenListenerRef.current) {
        tokenListenerRef.current(); // Cleanup listener
      }
    };
  }, []);

  /**
   * Initialize admin authentication state from stored tokens
   */
  const initializeAdminAuth = async () => {
    try {
      mountedRef.current = true;
      setIsLoading(true);
      setError(null);

      // Check for existing admin token (direct localStorage - not enhancedTokenManager)
      try {
        const adminToken = localStorage.getItem('admin_token');
        if (adminToken) {
          await loadAdminFromToken(adminToken);
        }
      } catch (error) {
        console.warn('Failed to load admin token:', error);
      }

      // Set up token manager listeners for admin tokens
      if (mountedRef.current) {
        setupTokenManagerListeners();
      }

    } catch (error) {
      console.error('Failed to initialize admin auth:', error);
      setError('Failed to initialize admin authentication');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Set up token manager event listeners for admin tokens
   */
  const setupTokenManagerListeners = () => {
    if (!mountedRef.current) return;

    tokenListenerRef.current = enhancedTokenManager.addEventListener((event) => {
      if (!mountedRef.current) return;

      // Only handle admin token events
      if (event.tokenType !== 'admin_token') return;

      switch (event.type) {
        case 'session_warning':
          handleSessionWarning(event);
          break;
        case 'token_change':
          handleTokenChange(event);
          break;
        case 'token_expired':
          handleTokenExpired(event);
          break;
        default:
          break;
      }
    });
  };

  /**
   * Handle session warning events
   */
  const handleSessionWarning = (event) => {
    if (!mountedRef.current) return;

    safeSetState(setSessionWarning, {
      threshold: event.threshold,
      timeRemaining: event.timeRemaining,
      minutes: event.minutes,
      message: event.message
    });

    // Update session status based on threshold
    if (event.threshold === '2min') {
      safeSetState(setSessionStatus, 'expiring');
    } else if (event.threshold === '5min') {
      safeSetState(setSessionStatus, 'warning');
    } else {
      safeSetState(setSessionStatus, 'warning');
    }

    try {
      productionLogger.logSecurityEvent('admin_session_warning', {
        threshold: event.threshold,
        adminId: adminUser?.id || 'unknown',
        timestamp: Date.now()
      });
    } catch (error) {
      console.warn('Failed to log admin session warning:', error);
    }
  };

  /**
   * Handle token change events from other tabs
   */
  const handleTokenChange = async (event) => {
    if (!mountedRef.current || event.source !== 'other_tab') {
      return;
    }

    try {
      const adminToken = await enhancedTokenManager.getToken('admin_token');
      if (adminToken) {
        await loadAdminFromToken(adminToken);
      } else {
        safeSetState(setAdminUser, null);
        safeSetState(setIsAuthenticated, false);
      }
    } catch (error) {
      console.warn('Failed to handle admin token change:', error);
    }
  };

  /**
   * Handle token expiration
   */
  const handleTokenExpired = (event) => {
    if (!mountedRef.current) return;

    console.log('🔓 Admin token expired - clearing admin state and redirecting to login');

    safeSetState(setAdminUser, null);
    safeSetState(setIsAuthenticated, false);
    safeSetState(setSessionWarning, null);
    safeSetState(setSessionStatus, 'expired');

    // Clear admin tokens
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_token_expires');

    // Redirect to admin login if not already there
    if (window.location.pathname !== '/admin/login') {
      window.location.href = '/admin/login?reason=session_expired';
    }
  };

  /**
   * Load admin data from token
   */
  const loadAdminFromToken = async (token) => {
    try {
      const payload = enhancedTokenManager.decodeTokenPayload(token);

      // Validate this is an admin token
      if (!payload.is_admin) {
        console.warn('Token is not an admin token');
        return;
      }

      const adminData = {
        id: payload.sub,
        email: payload.email,
        role: payload.role,
        name: payload.name,
        permissions: payload.permissions || {},
        organizationId: payload.stytch_organization_id
      };

      setAdminUser(adminData);
      setIsAuthenticated(true);

    } catch (error) {
      console.error('Failed to load admin from token:', error);
      setAdminUser(null);
      setIsAuthenticated(false);
    }
  };

  /**
   * Admin login with email and password
   */
  const login = async (email, password) => {
    const perfOp = authPerformanceMonitor.startOperation('admin_login');

    try {
      safeSetState(setIsLoading, true);
      safeSetState(setError, null);

      const response = await apiClient.post('/admin/auth/login', {
        email,
        password
      });

      const data = response?.data ?? {};

      // Handle MFA required state
      if (data.mfa_required && data.member_authenticated === false) {
        const mfaPayload = {
          intermediateSessionToken: data.intermediate_session_token,
          sessionToken: data.session_token,
          sessionJwt: data.session_jwt,
          memberId: data.member_id,
          organizationId: data.organization_id,
          availableMethods: data.available_methods || ['sms_otp', 'totp'],
          email: data.email || email,
          role: data.role,
          permissions: data.permissions
        };

        authPerformanceMonitor.endOperation(perfOp, true, { mfaRequired: true });
        return { success: false, mfaRequired: true, data: mfaPayload };
      }

      // Successful login (no MFA or MFA already satisfied)
      if (data.success && data.access_token) {
        // Store token directly in localStorage (not enhancedTokenManager)
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(data.admin_user || {}));
        localStorage.setItem('admin_token_expires', Date.now() + ((data.expires_in || 3600) * 1000));

        await loadAdminFromToken(data.access_token);

        setStytchSessionToken(data.session_token || null);
        setStytchSessionJwt(data.session_jwt || null);

        safeSetState(setSessionWarning, null);
        safeSetState(setSessionStatus, 'active');

        const sessionId = securityMonitor.trackLogin(
          adminUser?.id || email,
          {
            email,
            userAgent: navigator.userAgent,
            isAdmin: true
          }
        );

        try {
          productionLogger.logSecurityEvent('admin_login', {
            adminId: adminUser?.id,
            email,
            sessionId,
            timestamp: Date.now(),
            viaMfa: false
          });
        } catch (logError) {
          console.warn('Failed to log admin login event:', logError);
        }

        authPerformanceMonitor.endOperation(perfOp, true);
        return { success: true, sessionId };
      }

      throw new Error(data.detail || 'Login failed');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Login failed';
      const errorMessage = Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail;
      safeSetState(setError, errorMessage);

      const lockoutStatus = securityMonitor.trackFailedLogin(email, errorMessage);

      try {
        productionLogger.logSecurityEvent('admin_login_failure', {
          email,
          error: errorMessage,
          lockoutStatus,
          timestamp: Date.now()
        });
      } catch (logError) {
        console.warn('Failed to log admin login failure:', logError);
      }

      authPerformanceMonitor.endOperation(perfOp, false, { error: errorMessage });

      if (lockoutStatus.shouldLockout) {
        const lockoutError = `Account locked due to too many failed attempts. Try again after ${new Date(lockoutStatus.nextAllowedAttempt).toLocaleTimeString()}`;
        safeSetState(setError, lockoutError);
        return { success: false, error: lockoutError, locked: true };
      }

      return {
        success: false,
        error: errorMessage,
        remainingAttempts: lockoutStatus.remainingAttempts
      };
    } finally {
      safeSetState(setIsLoading, false);
    }
  };

  /**
   * Send MFA challenge (SMS or TOTP)
   */
  const sendMfaChallenge = async ({
    method,
    organizationId,
    memberId,
    phoneNumber,
    intermediateSessionToken,
    sessionToken,
    sessionJwt,
    totpRegistrationId
  }) => {
    try {
      const payload = {
        method,
        organization_id: organizationId,
        member_id: memberId
      };

      if (phoneNumber) {
        payload.phone_number = phoneNumber;
      }
      if (intermediateSessionToken) {
        payload.intermediate_session_token = intermediateSessionToken;
      }
      if (sessionToken) {
        payload.session_token = sessionToken;
      }
      if (sessionJwt) {
        payload.session_jwt = sessionJwt;
      }
      if (totpRegistrationId) {
        payload.totp_registration_id = totpRegistrationId;
      }

      const response = await apiClient.post('/admin/auth/mfa/challenge', payload);
      return { success: true, data: response.data };
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Failed to send MFA challenge';
      const errorMessage = Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail;
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Complete MFA verification
   */
  const completeMfa = async ({
    method,
    code,
    organizationId,
    memberId,
    intermediateSessionToken,
    sessionToken,
    sessionJwt,
    totpRegistrationId,
    email,
    role,
    permissions
  }) => {
    const perfOp = authPerformanceMonitor.startOperation('admin_mfa_verify');

    try {
      safeSetState(setIsLoading, true);
      safeSetState(setError, null);

      const payload = {
        method,
        code,
        organization_id: organizationId,
        member_id: memberId,
        email,
        role,
        permissions
      };

      if (intermediateSessionToken) {
        payload.intermediate_session_token = intermediateSessionToken;
      }
      if (sessionToken) {
        payload.session_token = sessionToken;
      }
      if (sessionJwt) {
        payload.session_jwt = sessionJwt;
      }
      if (totpRegistrationId) {
        payload.totp_registration_id = totpRegistrationId;
      }

      const response = await apiClient.post('/admin/auth/mfa/verify', payload);
      const data = response?.data ?? {};
      console.log('[AdminAuthProvider] MFA verify response:', { success: data.success, hasToken: !!data.access_token, data });

      if (data.success && data.access_token) {
        // Store token directly in localStorage (not enhancedTokenManager)
        console.log('[AdminAuthProvider] MFA Success - Storing token');
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(data.admin_user || {}));
        localStorage.setItem('admin_token_expires', Date.now() + ((data.expires_in || 3600) * 1000));
        console.log('[AdminAuthProvider] Token stored. Verifying localStorage has it:', !!localStorage.getItem('admin_token'));

        await loadAdminFromToken(data.access_token);

        if (data.session_token || data.session_jwt) {
          setStytchSessionToken(data.session_token || null);
          setStytchSessionJwt(data.session_jwt || null);
        }

        safeSetState(setSessionWarning, null);
        safeSetState(setSessionStatus, 'active');

        const sessionId = securityMonitor.trackLogin(
          adminUser?.id || memberId || email,
          {
            email,
            userAgent: navigator.userAgent,
            method,
            viaMfa: true,
            isAdmin: true
          }
        );

        try {
          productionLogger.logSecurityEvent('admin_login', {
            adminId: adminUser?.id,
            email,
            sessionId,
            timestamp: Date.now(),
            viaMfa: true,
            mfaMethod: method
          });
        } catch (logError) {
          console.warn('Failed to log admin MFA login event:', logError);
        }

        authPerformanceMonitor.endOperation(perfOp, true, { viaMfa: true, method });
        return { success: true, sessionId };
      }

      throw new Error(data.detail || data.message || 'MFA verification failed');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'MFA verification failed';
      const errorMessage = Array.isArray(detail) ? detail.map(e => e.msg || e).join(', ') : detail;
      safeSetState(setError, errorMessage);

      if (email) {
        try {
          productionLogger.logSecurityEvent('admin_login_failure', {
            email,
            error: errorMessage,
            context: 'mfa_verify',
            timestamp: Date.now()
          });
        } catch (logError) {
          console.warn('Failed to log admin MFA failure event:', logError);
        }

        securityMonitor.trackFailedLogin(email, errorMessage);
      }

      authPerformanceMonitor.endOperation(perfOp, false, { error: errorMessage, method });
      return { success: false, error: errorMessage };
    } finally {
      safeSetState(setIsLoading, false);
    }
  };

  /**
   * Admin logout
   */
  const logout = async () => {
    try {
      safeSetState(setIsLoading, true);

      const currentAdminId = adminUser?.id;

      // Call admin logout API
      try {
        const token = await enhancedTokenManager.getToken('admin_token');
        if (token) {
          await apiClient.post('/admin/auth/logout', {}, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        }
      } catch (error) {
        console.warn('Admin logout API call failed:', error);
      }

      // Clear admin tokens and state (direct localStorage)
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_token_expires');
      safeSetState(setAdminUser, null);
      safeSetState(setIsAuthenticated, false);
      safeSetState(setSessionWarning, null);
      safeSetState(setSessionStatus, 'active');
      safeSetState(setError, null);
      setStytchSessionToken(null);
      setStytchSessionJwt(null);

      try {
        productionLogger.logSecurityEvent('admin_logout', {
          adminId: currentAdminId,
          timestamp: Date.now()
        });
      } catch (logError) {
        console.warn('Failed to log admin logout event:', logError);
      }

    } catch (error) {
      console.error('Admin logout error:', error);
    } finally {
      safeSetState(setIsLoading, false);
    }
  };

  /**
   * Check if admin has specific permission
   */
  const hasPermission = useCallback((permission) => {
    if (!adminUser || !adminUser.permissions) return false;

    // System admins have all permissions
    if (adminUser.role === 'system_admin') return true;

    return adminUser.permissions[permission] === true;
  }, [adminUser]);

  /**
   * Refresh admin session (extend expiry)
   */
  const refreshSession = async () => {
    try {
      // Admin tokens are stored directly in localStorage (not enhancedTokenManager)
      const token = localStorage.getItem('admin_token');
      if (!token) {
        console.warn('No admin token found in localStorage for refresh');
        return { success: false, error: 'No admin token found' };
      }

      const response = await apiClient.post('/admin/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = response?.data ?? {};

      if (data.success && data.access_token) {
        // Store refreshed token directly in localStorage (matching login pattern)
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(data.admin_user || {}));
        localStorage.setItem('admin_token_expires', Date.now() + ((data.expires_in || 3600) * 1000));

        await loadAdminFromToken(data.access_token);

        safeSetState(setSessionWarning, null);
        safeSetState(setSessionStatus, 'active');

        console.log('✅ Admin session refreshed successfully');
        return { success: true };
      }

      return { success: false, error: 'Failed to refresh session' };
    } catch (error) {
      console.error('Failed to refresh admin session:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    // State
    adminUser,
    isAuthenticated,
    isLoading,
    error,
    sessionWarning,
    sessionStatus,

    // Actions
    login,
    logout,
    sendMfaChallenge,
    completeMfa,
    hasPermission,
    refreshSession,

    // Utilities
    clearError: () => safeSetState(setError, null)
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthProvider;
